import React from 'react';

import {
  getMonthesNames,
  createMonth,
  getWeekDaysNames,
  getMonthNumberOfDays,
  createDate
} from '../../../../CustomCalendar/utils/helpers/date';

interface UseCalendarParams {
  locale?: string;
  // selectedDate: Date;
  selectedStartDate: Date;
  selectedEndDate: Date;
  firstWeekDayNumber?: number;
}

const DAYS_IN_WEEK = 7;

const getYearsInterval = (year: number) => {
  const startYear = Math.floor(year / 10) * 10;
  return [...Array(10)].map((_, index) => startYear + index);
};

export const useCalendar = ({
  locale = 'default',
  // selectedDate: date,
  selectedStartDate,
  selectedEndDate,
  firstWeekDayNumber = 2
}: UseCalendarParams) => {
  const [mode, setMode] = React.useState<'days' | 'monthes' | 'years'>('days');


  // const [selectedDay, setSelectedDay] = React.useState(createDate({ date }));

  const [selectedStartDay, setSelectedStartDay] = React.useState(createDate({ date: selectedStartDate }));
  const [selectedEndDay, setSelectedEndDay] = React.useState(createDate({ date: selectedEndDate }));


  const [selectedMonth, setSelectedMonth] = React.useState(
    // createMonth({ date: new Date(selectedDay.year, selectedDay.monthIndex), locale })
  );

  const [selectedStartMonth, setSelectedStartMonth] = React.useState(
    createMonth({ date: new Date(selectedStartDay.year, selectedStartDay.monthIndex), locale })
  );

  const [selectedEndMonth, setSelectedEndMonth] = React.useState(
    createMonth({ date: new Date(selectedEndDay.year, selectedEndDay.monthIndex), locale })
  );


  // const [selectedYear, setSelectedYear] = React.useState(selectedDay.year);



  const [selectedStartYear, setSelectedStartYear] = React.useState(selectedStartDay.year);
  const [selectedEndYear, setSelectedEndYear] = React.useState(selectedEndDay.year);

  const [selectedStartYearsInterval, setSelectedStartYearsInterval] = React.useState(
    getYearsInterval(selectedStartDay.year)
  );

  const [selectedEndYearsInterval, setSelectedEndYearsInterval] = React.useState(
    getYearsInterval(selectedEndDay.year)
  );



  // const [selectedYearsInterval, setSelectedYearsInterval] = React.useState(
  //   getYearsInterval(selectedDay.year)
  // );

  const monthesNames = React.useMemo(() => getMonthesNames(locale), []);
  const weekDaysNames = React.useMemo(() => getWeekDaysNames(firstWeekDayNumber, locale), []);

  // const days = React.useMemo(() => selectedMonth.createMonthDays(), [selectedMonth, selectedYear]);

  const startDays = React.useMemo(() => selectedStartMonth.createMonthDays(), [selectedStartMonth, selectedStartYear]);
  const endDays = React.useMemo(() => selectedEndMonth.createMonthDays(), [selectedEndMonth, selectedEndYear]);


  const calendarStartDays = React.useMemo(() => {
    const monthNumberOfDays = getMonthNumberOfDays(selectedStartMonth.monthIndex, selectedStartYear);

    const prevMonthDays = createMonth({
      date: new Date(selectedStartYear, selectedStartMonth.monthIndex - 1),
      locale
    }).createMonthDays();

    const nextMonthDays = createMonth({
      date: new Date(selectedStartYear, selectedStartMonth.monthIndex + 1),
      locale
    }).createMonthDays();

    const firstStartDay = startDays[0]
    const lastStartDay = startDays[monthNumberOfDays - 1]
    // const firstDay = days[0];
    // const lastDay = days[monthNumberOfDays - 1];

    const shiftIndex = firstWeekDayNumber - 1;
    const numberOfPrevDays =
      firstStartDay.dayNumberInWeek - 1 - shiftIndex < 0
        ? DAYS_IN_WEEK - (firstWeekDayNumber - firstStartDay.dayNumberInWeek)
        : firstStartDay.dayNumberInWeek - 1 - shiftIndex;

    const numberOfNextDays =
      DAYS_IN_WEEK - lastStartDay.dayNumberInWeek + shiftIndex > 6
        ? DAYS_IN_WEEK - lastStartDay.dayNumberInWeek - (DAYS_IN_WEEK - shiftIndex)
        : DAYS_IN_WEEK - lastStartDay.dayNumberInWeek + shiftIndex;

    const totalCalendarDays = startDays.length + numberOfPrevDays + numberOfNextDays;

    const result = [];

    for (let i = 0; i < numberOfPrevDays; i += 1) {
      const inverted = numberOfPrevDays - i;
      result[i] = prevMonthDays[prevMonthDays.length - inverted];
    }

    for (let i = numberOfPrevDays; i < totalCalendarDays - numberOfNextDays; i += 1) {
      result[i] = startDays[i - numberOfPrevDays];
    }

    for (let i = totalCalendarDays - numberOfNextDays; i < totalCalendarDays; i += 1) {
      result[i] = nextMonthDays[i - totalCalendarDays + numberOfNextDays];
    }

    return result;
  }, [selectedStartMonth.year, selectedStartMonth.monthIndex, selectedStartYear]);


  const calendarEndDays = React.useMemo(() => {
    const monthNumberOfDays = getMonthNumberOfDays(selectedEndMonth.monthIndex, selectedEndYear);

    const prevMonthDays = createMonth({
      date: new Date(selectedEndYear, selectedEndMonth.monthIndex - 1),
      locale
    }).createMonthDays();

    const nextMonthDays = createMonth({
      date: new Date(selectedEndYear, selectedEndMonth.monthIndex + 1),
      locale
    }).createMonthDays();

    const firstDay = endDays[0];
    const lastDay = endDays[monthNumberOfDays - 1];

    const shiftIndex = firstWeekDayNumber - 1;
    const numberOfPrevDays =
      firstDay.dayNumberInWeek - 1 - shiftIndex < 0
        ? DAYS_IN_WEEK - (firstWeekDayNumber - firstDay.dayNumberInWeek)
        : firstDay.dayNumberInWeek - 1 - shiftIndex;

    const numberOfNextDays =
      DAYS_IN_WEEK - lastDay.dayNumberInWeek + shiftIndex > 6
        ? DAYS_IN_WEEK - lastDay.dayNumberInWeek - (DAYS_IN_WEEK - shiftIndex)
        : DAYS_IN_WEEK - lastDay.dayNumberInWeek + shiftIndex;

    const totalCalendarDays = endDays.length + numberOfPrevDays + numberOfNextDays;

    const result = [];

    for (let i = 0; i < numberOfPrevDays; i += 1) {
      const inverted = numberOfPrevDays - i;
      result[i] = prevMonthDays[prevMonthDays.length - inverted];
    }

    for (let i = numberOfPrevDays; i < totalCalendarDays - numberOfNextDays; i += 1) {
      result[i] = endDays[i - numberOfPrevDays];
    }

    for (let i = totalCalendarDays - numberOfNextDays; i < totalCalendarDays; i += 1) {
      result[i] = nextMonthDays[i - totalCalendarDays + numberOfNextDays];
    }

    return result;
  }, [selectedEndMonth.year, selectedEndMonth.monthIndex, selectedEndYear]);

  const onClickArrowStart = (direction: 'right' | 'left') => {
    if (mode === 'years' && direction === 'left') {
      return setSelectedStartYearsInterval(getYearsInterval(selectedStartYearsInterval[0] - 10));
    }

    if (mode === 'years' && direction === 'right') {
      return setSelectedStartYearsInterval(getYearsInterval(selectedStartYearsInterval[0] + 10));
    }

    if (mode === 'monthes' && direction === 'left') {
      const year = selectedStartYear - 1;
      if (!selectedStartYearsInterval.includes(year)) setSelectedStartYearsInterval(getYearsInterval(year));
      return setSelectedStartYear(selectedStartYear - 1);
    }

    if (mode === 'monthes' && direction === 'right') {
      const year = selectedStartYear + 1;
      if (!selectedStartYearsInterval.includes(year)) setSelectedStartYearsInterval(getYearsInterval(year));
      return setSelectedStartYear(selectedStartYear + 1);
    }

    if (mode === 'days') {
      const monthIndex =
        direction === 'left' ? selectedStartMonth.monthIndex - 1 : selectedStartMonth.monthIndex + 1;
      if (monthIndex === -1) {
        const year = selectedStartYear - 1;
        setSelectedStartYear(year);
        if (!selectedStartYearsInterval.includes(year)) setSelectedStartYearsInterval(getYearsInterval(year));
        return setSelectedStartMonth(createMonth({ date: new Date(selectedStartYear - 1, 11), locale }));
      }

      if (monthIndex === 12) {
        const year = selectedStartYear + 1;
        setSelectedStartYear(year);
        if (!selectedStartYearsInterval.includes(year)) setSelectedStartYearsInterval(getYearsInterval(year));
        return setSelectedStartMonth(createMonth({ date: new Date(year, 0), locale }));
      }

      setSelectedStartMonth(createMonth({ date: new Date(selectedStartYear, monthIndex), locale }));
    }
  };

  const onClickArrowEnd = (direction: 'right' | 'left') => {
    if (mode === 'years' && direction === 'left') {
      return setSelectedEndYearsInterval(getYearsInterval(selectedEndYearsInterval[0] - 10));
    }

    if (mode === 'years' && direction === 'right') {
      return setSelectedEndYearsInterval(getYearsInterval(selectedEndYearsInterval[0] + 10));
    }

    if (mode === 'monthes' && direction === 'left') {
      const year = selectedEndYear - 1;
      if (!selectedEndYearsInterval.includes(year)) setSelectedEndYearsInterval(getYearsInterval(year));
      return setSelectedEndYear(selectedEndYear - 1);
    }

    if (mode === 'monthes' && direction === 'right') {
      const year = selectedEndYear + 1;
      if (!selectedEndYearsInterval.includes(year)) setSelectedEndYearsInterval(getYearsInterval(year));
      return setSelectedEndYear(selectedEndYear + 1);
    }

    if (mode === 'days') {
      const monthIndex =
        direction === 'left' ? selectedEndMonth.monthIndex - 1 : selectedEndMonth.monthIndex + 1;
      if (monthIndex === -1) {
        const year = selectedEndYear - 1;
        setSelectedEndYear(year);
        if (!selectedEndYearsInterval.includes(year)) setSelectedEndYearsInterval(getYearsInterval(year));
        return setSelectedEndMonth(createMonth({ date: new Date(selectedEndYear - 1, 11), locale }));
      }

      if (monthIndex === 12) {
        const year = selectedEndYear + 1;
        setSelectedEndYear(year);
        if (!selectedEndYearsInterval.includes(year)) setSelectedEndYearsInterval(getYearsInterval(year));
        return setSelectedEndMonth(createMonth({ date: new Date(year, 0), locale }));
      }

      setSelectedEndMonth(createMonth({ date: new Date(selectedEndYear, monthIndex), locale }));
    }
  };




  // const onClickArrow = (direction: 'right' | 'left') => {
  //   if (mode === 'years' && direction === 'left') {
  //     return setSelectedYearsInterval(getYearsInterval(selectedYearsInterval[0] - 10));
  //   }

  //   if (mode === 'years' && direction === 'right') {
  //     return setSelectedYearsInterval(getYearsInterval(selectedYearsInterval[0] + 10));
  //   }

  //   if (mode === 'monthes' && direction === 'left') {
  //     const year = selectedYear - 1;
  //     if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
  //     return setSelectedYear(selectedYear - 1);
  //   }

  //   if (mode === 'monthes' && direction === 'right') {
  //     const year = selectedYear + 1;
  //     if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
  //     return setSelectedYear(selectedYear + 1);
  //   }

  //   if (mode === 'days') {
  //     const monthIndex =
  //       direction === 'left' ? selectedMonth.monthIndex - 1 : selectedMonth.monthIndex + 1;
  //     if (monthIndex === -1) {
  //       const year = selectedYear - 1;
  //       setSelectedYear(year);
  //       if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
  //       return setSelectedMonth(createMonth({ date: new Date(selectedYear - 1, 11), locale }));
  //     }

  //     if (monthIndex === 12) {
  //       const year = selectedYear + 1;
  //       setSelectedYear(year);
  //       if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
  //       return setSelectedMonth(createMonth({ date: new Date(year, 0), locale }));
  //     }

  //     setSelectedMonth(createMonth({ date: new Date(selectedYear, monthIndex), locale }));
  //   }
  // };

  const setSelectedStartMonthByIndex = (monthIndex: number) => {
    setSelectedStartMonth(createMonth({ date: new Date(selectedStartYear, monthIndex), locale }));
  };

  const setSelectedEndMonthByIndex = (monthIndex: number) => {
    setSelectedEndMonth(createMonth({ date: new Date(selectedEndYear, monthIndex), locale }));
  };



  const clearSelection = () => {
    setSelectedStartDay(null as never);
    setSelectedEndDay(null as never);
  };


  return {
    state: {
      mode,
      // calendarDays,
      calendarEndDays,
      calendarStartDays,
      weekDaysNames,
      monthesNames,
      // selectedDay,
      selectedMonth,
      // selectedYear,
      // selectedYearsInterval,

      selectedStartDay,
      selectedEndDay,
      selectedStartMonth,
      selectedEndMonth,
      selectedStartYear,
      selectedEndYear,
      selectedStartYearsInterval,
      selectedEndYearsInterval
    },
    functions: {
      onClickArrowStart,
      onClickArrowEnd,
      // onClickArrow,
      setMode,
      // setSelectedDay,
      // setSelectedMonthByIndex,
      // setSelectedYear,
      // setSelectedYearsInterval,
      setSelectedEndDay,
      setSelectedStartDay,
      setSelectedStartYear,
      setSelectedEndYear,
      setSelectedStartYearsInterval,
      setSelectedEndYearsInterval,
      clearSelection,
      setSelectedStartMonth,
      setSelectedEndMonth,
      setSelectedEndMonthByIndex,
      setSelectedStartMonthByIndex
    }
  };
};
